import "./board.css";
import { ArrowDownFill, ChevronDownFill, ChevronUpFill } from "../../assets";
import { Icon } from "../../components";
import { Placeholder } from "../loading";
import { useLanguage } from "../../hooks";

// BOARD -----------------
interface BoardProps {
  id: string;
  error?: boolean;
  children: React.ReactNode;
}

export const Board: React.FC<BoardProps> = ({ id, children, error }) => {
  const { t } = useLanguage();

  return (
    <>
      <section className="board-wrapper scroll-container">
        {error ? (
          <>
            <div className="board-error">
              <p>{t.components.errorFetchData}</p>
            </div>
          </>
        ) : (
          <table className="board" id={id}>
            {children}
          </table>
        )}
      </section>
    </>
  );
};

// HEAD -----------------
interface BoardHeadProps {
  children: React.ReactNode;
}

export const BoardHead: React.FC<BoardHeadProps> = ({ children }) => {
  return (
    <>
      <thead className="board-thead">{children}</thead>
    </>
  );
};

// BODY -----------------
interface BoardBodyProps {
  isMobile?: boolean;
  isLoading?: boolean;
  children: React.ReactNode;
}

export const BoardBody: React.FC<BoardBodyProps> = ({
  isMobile = false,
  isLoading = false,
  children,
}) => {
  return (
    <>
      <tbody className={`board-tbody ${isMobile ? "mobile" : ""}`}>
        {isLoading ? (
          <BoardTr>
            <BoardTd>
              <Placeholder.Spin />
            </BoardTd>
          </BoardTr>
        ) : (
          children
        )}
      </tbody>
    </>
  );
};

// TR -----------------
interface BoardTrProps {
  children: React.ReactNode;
}

export const BoardTr: React.FC<BoardTrProps> = ({ children }) => {
  return (
    <>
      <tr>{children}</tr>
    </>
  );
};

// TH -----------------
interface BoardThProps {
  className?: string;
  onClick?: () => void;
  text?: string;
}

export const BoardTh: React.FC<BoardThProps> = (props) => {
  return (
    <>
      <th className={props?.className}>
        {props.onClick ? (
          <button
            className="board-button-reorder board-card-direction"
            onClick={props.onClick}
          >
            <h4>{props.text}</h4>
            <Icon icon={ArrowDownFill} className="board-button-reorder-icon" />
          </button>
        ) : (
          <h4 className="board-card-direction board-th-static">{props.text}</h4>
        )}
      </th>
    </>
  );
};

// TD -----------------

interface BoardTdProps {
  className?: string;
  hasClassNameDirection?: boolean;
  hasClassNameW100?: boolean;
  children: React.ReactNode;
  mobile?: {
    isMobile: boolean;
    isActive: boolean;
    onClick: () => void;
    text: string;
  };
}

export const BoardTd: React.FC<BoardTdProps> = ({
  children,
  className,
  hasClassNameDirection = true,
  hasClassNameW100 = false,
  mobile,
}) => {
  return (
    <>
      {mobile !== undefined && mobile.isMobile ? (
        <td>
          <button
            className={`board-card-mobile-head board-card-direction ${
              mobile.isActive ? "active" : ""
            }`}
            onClick={mobile.onClick}
          >
            <p className="board-bold">{mobile.text}</p>
            <Icon
              icon={mobile.isActive ? ChevronUpFill : ChevronDownFill}
              alt="Alternate visibility"
              className="board-card-mobile-head-icon"
            />
          </button>

          <div
            className={`board-card-mobile-info-wrapper ${
              mobile.isActive ? "active board-card-direction" : ""
            }`}
          >
            {children}
          </div>
        </td>
      ) : (
        <td
          className={`${className} ${
            hasClassNameDirection ? "board-card-direction" : ""
          } ${hasClassNameW100 ? "board-card-w100" : ""}`}
        >
          {children}
        </td>
      )}
    </>
  );
};

// TD MOBILE ITEM -----------------

interface BoardTdMobileItemProps {
  title: string;
  children: React.ReactNode;
}

export const BoardTdMobileItem: React.FC<BoardTdMobileItemProps> = (props) => {
  return (
    <>
      <div className="board-card-mobile-info-item">
        <h4>{props.title}</h4>
        {props.children}
      </div>
    </>
  );
};

// DOT STATUS -----------------

interface BoardDotStatusProps {
  pColor: string; // PRIMARY COLOR
  sColor: string; // SECONDARY COLOR
  text: string;
}

export const BoardDotStatus: React.FC<BoardDotStatusProps> = (props) => {
  return (
    <div className="board-status-wrapper">
      <div
        className={`board-status`}
        style={{
          color: props.pColor,
          backgroundColor: props.sColor,
        }}
      >
        <div
          className="board-status-dot"
          style={{ backgroundColor: props.pColor }}
        />
        {props.text}
      </div>
    </div>
  );
};
